import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import './GameLeaderboardsPage.css';
import { ClompApi } from '../api/ClompApi';

const api = new ClompApi();

// The boards for each game. Space Jump's are filled from the backend (players who have signed the game in to their
// account) and so are Clompy Clicker's; Solarflare's will be once it reports runs, and until then show their empty
// state. `empty` is what a board says when nobody is on it.
const GAMES = [
    {
        id: 'solarflare',
        name: 'Solarflare',
        empty: 'No Solarflare runs have been recorded yet. Check back soon!',
        boards: [
            { id: 'speedrun', name: 'Speedruns', value: 'Time' },
            { id: 'stats', name: 'Player stats', value: 'Score' }
        ]
    },
    {
        id: 'spacejump',
        name: 'Space Jump',
        empty: 'No Space Jump runs have been recorded yet. Check back soon!',
        boards: [
            { id: 'fastest', name: 'Fastest clear', value: 'Time' },
            { id: 'jumps', name: 'Fewest jumps', value: 'Jumps' }
        ]
    },
    {
        id: 'clompyclicker',
        name: 'Clompy Clicker',
        empty: 'Nobody has collected any diamonds yet. Be the first!',
        boards: [
            { id: 'diamonds', name: 'All-time diamonds', value: 'Diamonds' }
        ]
    }
];

// The game a link asked for with ?game=<id> (as the Clompy Clicker game's Leaderboard button does), or the first
function gameFromAddress(search) {
    let wanted = new URLSearchParams(search).get('game');
    return GAMES.find((game) => game.id === wanted) || GAMES[0];
}

export class GameLeaderboardsPage extends React.Component {

    state = {
        game: gameFromAddress(this.props.location.search).id,
        board: gameFromAddress(this.props.location.search).boards[0].id,
        // Space Jump: the reply from GET /spacejump/leaderboard, and which of its boards (full run or a level) is shown
        spaceJump: null,
        spaceJumpLevel: 'run',
        // Clompy Clicker: the players who have earned the most diamonds of all time, from GET /clicker/leaderboard
        clicker: []
    };

    componentDidMount() {
        api.games.getSpaceJumpLeaderboard().then((data) => {
            this.setState({ spaceJump: data, spaceJumpLevel: data.run });
        }).catch((err) => {
            // Leave the boards on their empty state
            console.log(err);
        });
        api.games.getClickerLeaderboard().then((data) => {
            this.setState({ clicker: data.entries });
        }).catch((err) => {
            console.log(err);
        });
    }

    // Seconds as minutes:seconds.tenths, the way the game shows run times
    formatTime(seconds) {
        let tenths = Math.floor(seconds * 10);
        let wholeSeconds = Math.floor(tenths / 10) % 60;
        return `${Math.floor(tenths / 600)}:${wholeSeconds < 10 ? '0' : ''}${wholeSeconds}.${tenths % 10}`;
    }

    // The rows for a board: [{ id, player, value, date, mods }], already formatted for display; mods are the
    // { id, name } of the game mods that were on for that score
    entriesFor(game, board) {
        if (game.id === 'clompyclicker') {
            return this.state.clicker.map((entry) => ({
                id: entry.id,
                player: entry.player,
                value: entry.value.toLocaleString(),
                date: new Date(entry.date).toLocaleDateString(),
                mods: []
            }));
        }
        if (game.id !== 'spacejump' || !this.state.spaceJump) {
            return [];
        }
        let boards = this.state.spaceJump.boards[this.state.spaceJumpLevel] || {};
        let modNames = this.state.spaceJump.mods || [];
        return (boards[board.id] || []).map((entry) => ({
            id: entry.id,
            player: entry.player,
            value: board.id === 'fastest' ? this.formatTime(entry.value) : entry.value,
            date: new Date(entry.date).toLocaleDateString(),
            mods: modNames.filter((mod) => (entry.mods || []).includes(mod.id))
        }));
    }

    profilePath(entry) {
        return `/profile/${encodeURIComponent(entry.player)}`;
    }

    selectGame(game) {
        this.setState({ game: game.id, board: game.boards[0].id });
    }

    renderBoard(game, board) {
        let entries = this.entriesFor(game, board);
        return (
            <div className="glb-board">
                <table className="glb-table">
                    <thead>
                        <tr>
                            <th className="glb-rank">Rank</th>
                            <th>Player</th>
                            <th className="glb-value">{board.value}</th>
                            <th className="glb-date">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, i) => (
                            // the whole row opens the player's profile; the name is the link for keyboards and screen readers
                            <tr key={entry.id} className="glb-row" onClick={() => this.props.history.push(this.profilePath(entry))}>
                                <td className="glb-rank">{i + 1}</td>
                                <td>
                                    <Link className="glb-player" to={this.profilePath(entry)} onClick={(e) => e.stopPropagation()}>{entry.player}</Link>
                                    {(entry.mods || []).map((mod) => (
                                        <img key={mod.id} className="glb-mod" src={`/img/spacejump/mods/${mod.id}.svg`}
                                            alt={mod.name} title={`${mod.name} mod`} />
                                    ))}
                                </td>
                                <td className="glb-value">{entry.value}</td>
                                <td className="glb-date">{entry.date}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {entries.length === 0 ?
                    <p className="glb-empty">{game.empty}</p>
                    : null}
            </div>
        );
    }

    render() {
        let game = GAMES.find((g) => g.id === this.state.game);
        let board = game.boards.find((b) => b.id === this.state.board);

        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className={`glb-page glb-${game.id}`}>
                    <div className="glb-page-header">
                        <p className="glb-eyebrow">Clomp games</p>
                        <h1>Leaderboard</h1>
                        <p className="glb-intro">Speedruns, stats and high scores from Clomp players.</p>
                    </div>
                    <div className="glb-tabs" role="tablist" aria-label="Game">
                        {GAMES.map((g) => (
                            <button key={g.id} type="button" role="tab" aria-selected={g.id === game.id}
                                className={g.id === game.id ? 'is-active' : ''} onClick={() => this.selectGame(g)}>
                                {g.name}
                            </button>
                        ))}
                    </div>
                    <div className="glb-boards" aria-label="Board">
                        {game.boards.map((b) => (
                            <button key={b.id} type="button" aria-pressed={b.id === board.id}
                                className={b.id === board.id ? 'is-active' : ''} onClick={() => this.setState({ board: b.id })}>
                                {b.name}
                            </button>
                        ))}
                    </div>
                    {game.id === 'spacejump' && this.state.spaceJump ?
                        <label className="glb-level">
                            Level
                            <select value={this.state.spaceJumpLevel} onChange={(e) => this.setState({ spaceJumpLevel: e.target.value })}>
                                <option value={this.state.spaceJump.run}>Full run (all 10 levels)</option>
                                {this.state.spaceJump.levels.map((level, i) => (
                                    <option key={level.id} value={level.id}>{i + 1}. {level.name}</option>
                                ))}
                            </select>
                        </label>
                        : null}
                    {this.renderBoard(game, board)}
                </div>
            </>
        );
    }

}
